import { Control, type ControlOptions } from "../core/Control.js";
import type { KeyEventArgs, MouseEventArgs } from "../core/events.js";
import type { Point, Rect, Size } from "../../common/geometry.js";
import type { RenderContext } from "../render/RenderContext.js";
import type { Theme } from "../themes/Theme.js";
import { isSelectable, parseMnemonic, type MenuItemSpec } from "./MenuItem.js";
import { fitInWorkArea, MenuPopup } from "./MenuPopup.js";

export interface MenuBarOptions extends ControlOptions {
  items?: MenuItemSpec[];
}

const TITLE_PADDING = 11;
/** Room above and below a menu title. Chosen to leave the default bar 26px at the default font. */
const TITLE_HEIGHT_PADDING = 5;

/**
 * A horizontal strip of top-level menu titles.
 *
 * Behaves the way a Windows menu bar does: click a title to drop its menu,
 * then sliding along the bar switches menus without another click, and Alt+
 * the underlined letter opens one from the keyboard.
 */
export class MenuBar extends Control {
  #items: MenuItemSpec[];
  #hover = -1;
  #openIndex = -1;
  #menu: MenuPopup | null = null;

  constructor(options: MenuBarOptions = {}) {
    super({ height: 26, width: 400, tabStop: false, cursor: "default", ...options });
    this.#items = options.items ?? [];

    this.on("pointermove", (e) => this.#onPointerMove(e));
    this.on("pointerleave", () => {
      if (this.#hover !== -1 && this.#openIndex < 0) {
        this.#hover = -1;
        this.invalidate();
      }
    });
    this.on("pointerdown", (e) => this.#onPointerDown(e));
  }

  get items(): MenuItemSpec[] {
    return this.#items;
  }
  set items(v: MenuItemSpec[]) {
    this.#items = v;
    this.close();
    this.invalidate();
  }

  get openIndex(): number {
    return this.#openIndex;
  }

  // --- geometry ---------------------------------------------------------

  /** Width of title `index`, measured with the theme's menu font. */
  titleWidth(index: number, rc: RenderContext, theme: Theme): number {
    const label = parseMnemonic(this.#items[index]?.text ?? "");
    return Math.ceil(rc.measureText(label.text, theme.metrics.font).width) + TITLE_PADDING * 2;
  }

  titleRect(index: number, rc: RenderContext, theme: Theme): Rect {
    let x = 2;
    for (let i = 0; i < index; i++) x += this.titleWidth(i, rc, theme);
    return { x, y: 2, width: this.titleWidth(index, rc, theme), height: this.height - 4 };
  }

  indexAt(p: Point, rc: RenderContext, theme: Theme): number {
    let x = 2;
    for (let i = 0; i < this.#items.length; i++) {
      const w = this.titleWidth(i, rc, theme);
      if (p.x >= x && p.x < x + w && p.y >= 0 && p.y < this.height) return i;
      x += w;
    }
    return -1;
  }

  // --- opening and closing ----------------------------------------------

  open(index: number): void {
    const desktop = this.desktop;
    const item = this.#items[index];
    if (!desktop || !item || !isSelectable(item) || !item.items?.length) return;
    if (this.#openIndex === index) return;

    this.close();
    const theme = desktop.theme;
    const menu = new MenuPopup({
      items: item.items,
      onDismiss: () => this.#onMenuDismissed(),
    });
    const size = menu.measureIn(desktop);
    const anchor = this.screenBounds;
    const title = this.titleRect(index, desktop.rc, theme);
    const area = desktop.workArea;

    const below = anchor.y + anchor.height;
    const y = below + size.height <= area.y + area.height ? below : anchor.y - size.height;
    const at = fitInWorkArea(desktop, { x: anchor.x + title.x, y }, size);

    menu.setBounds(at.x, at.y, size.width, size.height);
    // Dismissal can come from anywhere — a press outside, Escape, another
    // popup replacing the stack — so track the teardown rather than assuming
    // this class is the only thing that closes it.
    menu.on("closed", () => {
      if (this.#menu === menu) this.#onMenuDismissed();
    });
    this.#menu = menu;
    this.#openIndex = index;
    this.#hover = index;
    desktop.showPopup(menu);
    menu.focus();
    this.invalidate();
  }

  close(): void {
    const menu = this.#menu;
    this.#menu = null;
    this.#openIndex = -1;
    if (menu) this.desktop?.closePopup(menu);
    this.invalidate();
  }

  #onMenuDismissed(): void {
    this.#menu = null;
    this.#openIndex = -1;
    this.invalidate();
  }

  /** Open the menu whose title carries mnemonic `char`. Returns true if found. */
  openMnemonic(char: string): boolean {
    const key = char.toLowerCase();
    for (let i = 0; i < this.#items.length; i++) {
      const item = this.#items[i]!;
      if (!isSelectable(item)) continue;
      if (parseMnemonic(item.text ?? "").mnemonic === key) {
        this.open(i);
        this.#menu?.moveHighlight(1);
        return true;
      }
    }
    return false;
  }

  /**
   * Handle Alt+letter from a window's key handler. Wire this up from the
   * owning window so the bar responds without needing focus.
   */
  handleMenuKey(e: KeyEventArgs): boolean {
    if (!e.altKey || e.ctrlKey || e.metaKey || e.key.length !== 1) return false;
    return this.openMnemonic(e.key);
  }

  // --- input ------------------------------------------------------------

  protected override hitTestSelf(): boolean {
    return true;
  }

  #onPointerMove(e: MouseEventArgs): void {
    const desktop = this.desktop;
    if (!desktop) return;
    const index = this.indexAt({ x: e.x, y: e.y }, desktop.rc, desktop.theme);
    if (index === this.#hover) return;
    this.#hover = index;
    this.invalidate();
    // With a menu already down, sliding along the bar switches to the next
    // one without a second click — the Windows behavior.
    if (this.#openIndex >= 0 && index >= 0 && index !== this.#openIndex) this.open(index);
  }

  #onPointerDown(e: MouseEventArgs): void {
    const desktop = this.desktop;
    if (!desktop) return;
    const index = this.indexAt({ x: e.x, y: e.y }, desktop.rc, desktop.theme);
    e.handled = true;
    if (index < 0) return;
    // Clicking the open title closes it; the popup stack has already torn the
    // menu down by the time this runs, so compare against the remembered index.
    if (index === this.#openIndex) this.close();
    else this.open(index);
  }

  // --- painting ---------------------------------------------------------


  /**
   * As wide as it is given, and as tall as one line of the menu font needs.
   *
   * A bar is chrome: it has no say in how wide it is, and the only thing it can usefully answer
   * is how much of the edge it needs — which follows the theme's font, so a bigger font pushes
   * the content down instead of clipping the titles.
   */
  override preferredSize(available: Size): Size {
    const desktop = this.desktop;
    if (!desktop) return this.size;
    const line = desktop.rc.lineHeight(desktop.theme.metrics.font);
    return { width: available.width, height: Math.ceil(line + TITLE_HEIGHT_PADDING * 2) };
  }

  protected override onEnvironmentChanged(): void {
    this.invalidateMeasure();
  }

  protected override onPaint(rc: RenderContext, theme: Theme): void {
    const p = theme.palette;
    rc.fillRect(this.localRect, p.windowBg);

    const font = theme.metrics.font;
    for (let i = 0; i < this.#items.length; i++) {
      const item = this.#items[i]!;
      const r = this.titleRect(i, rc, theme);
      const enabled = isSelectable(item);
      const open = i === this.#openIndex;
      const hot = enabled && (open || i === this.#hover);

      if (hot) {
        theme.drawListItem(rc, r, {
          hovered: true,
          pressed: open,
          focused: false,
          disabled: false,
          selected: open,
          active: true,
        });
      }
      const ink = !enabled
        ? p.textDisabled
        : open
          ? theme.listItemTextColor({
              hovered: true,
              pressed: true,
              focused: false,
              disabled: false,
              selected: true,
              active: true,
            })
          : p.text;

      const label = parseMnemonic(item.text ?? "");
      const midY = r.y + r.height / 2;
      rc.drawText(label.text, r.x + TITLE_PADDING, midY, font, ink, { baseline: "middle" });

      if (label.mnemonicIndex >= 0) {
        const before = rc.measureText(label.text.slice(0, label.mnemonicIndex), font).width;
        const w = rc.measureText(label.text[label.mnemonicIndex]!, font).width;
        const m = rc.measureText(label.text, font);
        rc.hLine(r.x + TITLE_PADDING + before, Math.round(midY + m.height / 2), Math.ceil(w), ink);
      }
    }

    rc.hLine(0, this.height - 1, this.width, p.gridLine);
  }
}
